import { ShieldCheck, Lock, FileCheck2, UserCheck } from 'lucide-react';

const gates = [
  {
    icon: FileCheck2,
    label: 'Payer Authorization',
    description: 'Every code is pre-authorized before surgery.',
  },
  {
    icon: ShieldCheck,
    label: 'Clinical Documentation',
    description: 'Evidence extracted directly from the operative note.',
  },
  {
    icon: UserCheck,
    label: 'Surgeon Confirmation',
    description: 'Explicit attestation before any code reaches billing.',
  },
];

const ComplianceBlock = () => {
  return (
    <section className="py-20 md:py-28 bg-secondary">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Text — left-aligned */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-primary/15 bg-primary/5 mb-6">
              <Lock className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-primary tracking-wide uppercase">Immutable Gate</span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-5">
              Compliance Without Compromise.
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Built on 40 years of clinical experience, our platform enforces an immutable gate.
              No claim is exported without a perfect match between Payer Authorization,
              Clinical Documentation, and Surgeon Confirmation.
            </p>

            <div className="inline-flex items-center gap-3 px-4 py-3 rounded-md border border-primary/15 bg-primary/5">
              <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Verified Audit Trail</p>
                <p className="text-xs text-muted-foreground">Chronological, append-only, non-deletable</p>
              </div>
            </div>
          </div>

          {/* Right: Gates */}
          <div className="space-y-4">
            {gates.map((gate, index) => (
              <div key={gate.label} className="relative">
                {index < gates.length - 1 && (
                  <div className="absolute left-6 top-full w-px h-4 bg-border z-0" />
                )}
                <div className="relative bg-card border border-border rounded-md p-5 flex items-start gap-4 transition-all hover:shadow-lg">
                  <div className="flex-shrink-0 w-12 h-12 rounded-md bg-primary/5 border border-primary/15 flex items-center justify-center">
                    <gate.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-mono text-muted-foreground tracking-widest">
                      GATE {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-base font-bold text-foreground tracking-tight">{gate.label}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{gate.description}</p>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 rounded-md bg-primary/10 border border-primary/15 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-6 p-4 rounded-md border border-primary/20 bg-primary/5 text-center">
              <p className="text-xs font-mono text-primary tracking-widest uppercase mb-1">Export Gate Status</p>
              <p className="text-sm font-semibold text-foreground">
                Authorization = Documentation = Export. <span className="text-primary">Always.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComplianceBlock;
